import {
  Building2,
  CheckCircle2,
  Coins,
  Hourglass,
  Sparkles,
  UsersRound,
} from "lucide-react";
import { cn } from "@/lib/utils";

/** Peso formatter — ₱X,XXX (no decimals). Backend returns centavos. */
const formatPeso = (centavos) => {
  const pesos = Math.round((centavos || 0) / 100);
  return `₱${pesos.toLocaleString("en-PH")}`;
};

/** Reusable bento card. Sizes chosen so the grid feels naturally weighted. */
function BentoCard({
  testId,
  className,
  tone = "navy",
  icon: Icon,
  label,
  value,
  subtitle,
  children,
}) {
  const toneClasses = {
    navy: "border-brand-navy/10",
    green: "border-brand-green/20",
    plain: "border-gray-100",
  }[tone];
  const iconTone = {
    navy: "bg-brand-navy/8 text-brand-navy",
    green: "bg-brand-green/10 text-brand-green",
    plain: "bg-gray-100 text-gray-500",
  }[tone];
  return (
    <div
      className={cn(
        "relative overflow-hidden rounded-2xl border bg-white p-6 shadow-mb-card",
        toneClasses,
        className
      )}
      data-testid={testId}
    >
      {Icon && (
        <span
          className={cn(
            "inline-flex h-9 w-9 items-center justify-center rounded-full",
            iconTone
          )}
        >
          <Icon className="h-4 w-4" />
        </span>
      )}
      <div className="mt-4 text-[11px] font-semibold uppercase tracking-wider text-gray-500">
        {label}
      </div>
      {value !== undefined && (
        <div
          className="mt-1 text-3xl font-bold tracking-tight text-gray-900 tabular-nums sm:text-4xl"
          data-testid={testId ? `${testId}-value` : undefined}
        >
          {value}
        </div>
      )}
      {subtitle && (
        <div className="mt-1 text-xs text-gray-500">{subtitle}</div>
      )}
      {children}
    </div>
  );
}

export function MetricsBento({ metrics }) {
  if (!metrics) return null;
  const { users, revenue, liquidity } = metrics;
  return (
    <div
      className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4 lg:grid-rows-2"
      data-testid="admin-metrics-bento"
    >
      {/* Revenue takes the biggest tile (2x2 on lg) */}
      <BentoCard
        testId="kpi-mrr"
        tone="green"
        icon={Coins}
        label="Monthly recurring revenue"
        value={formatPeso(revenue.mrr_centavos)}
        subtitle={`Normalized monthly · ${revenue.active_pro} active pro subscription${revenue.active_pro === 1 ? "" : "s"}`}
        className="lg:col-span-2 lg:row-span-2"
      >
        <div className="mt-6 grid grid-cols-3 gap-3 border-t border-brand-green/15 pt-5 text-xs">
          <div>
            <div className="font-semibold uppercase tracking-wider text-gray-500 text-[10px]">
              Active Pro
            </div>
            <div
              className="mt-1 text-xl font-bold text-brand-green tabular-nums"
              data-testid="kpi-active-pro-value"
            >
              {revenue.active_pro}
            </div>
          </div>
          <div>
            <div className="font-semibold uppercase tracking-wider text-gray-500 text-[10px]">
              Trialing
            </div>
            <div
              className="mt-1 text-xl font-bold text-gray-900 tabular-nums"
              data-testid="kpi-trialing-value"
            >
              {revenue.trialing_active}
            </div>
          </div>
          <div>
            <div className="font-semibold uppercase tracking-wider text-gray-500 text-[10px]">
              Trial Expired
            </div>
            <div
              className="mt-1 text-xl font-bold text-gray-900 tabular-nums"
              data-testid="kpi-trial-expired-value"
            >
              {revenue.trial_expired}
            </div>
          </div>
        </div>
      </BentoCard>

      {/* Users — total */}
      <BentoCard
        testId="kpi-users-total"
        tone="navy"
        icon={UsersRound}
        label="Total sign-ups"
        value={users.total.toLocaleString("en-PH")}
        subtitle={`${users.verified.toLocaleString("en-PH")} verified`}
      />

      {/* Verifications pending */}
      <BentoCard
        testId="kpi-pending"
        tone="green"
        icon={Hourglass}
        label="Pending verifications"
        value={users.pending.toLocaleString("en-PH")}
        subtitle={
          users.rejected > 0
            ? `${users.rejected.toLocaleString("en-PH")} previously rejected`
            : "Rejected & purged excluded"
        }
      />

      {/* Liquidity: active listings */}
      <BentoCard
        testId="kpi-active-listings"
        tone="navy"
        icon={Building2}
        label="Active listings"
        value={liquidity.active_listings.toLocaleString("en-PH")}
        subtitle="Marketplace supply"
      />

      {/* Liquidity: matches & closed deals combined */}
      <BentoCard
        testId="kpi-matches"
        tone="green"
        icon={Sparkles}
        label="Total matches"
        value={liquidity.total_matches.toLocaleString("en-PH")}
        subtitle="Generated all-time"
      >
        <div className="mt-4 flex items-center justify-between rounded-xl border border-brand-green/20 bg-brand-green/5 px-3 py-2 text-xs">
          <span className="inline-flex items-center gap-1.5 font-semibold text-brand-green">
            <CheckCircle2 className="h-3.5 w-3.5" />
            Deals closed
          </span>
          <span
            className="text-lg font-bold text-brand-green tabular-nums"
            data-testid="kpi-closed-deals-value"
          >
            {liquidity.closed_deals}
          </span>
        </div>
      </BentoCard>
    </div>
  );
}
